import {
  InsurancePlanHelper as Datanac_InsurancePlanHelper,
  AipPassApiHelper as DatanacAipPassApiHelper,
  ApiHelper as DatanacApiHelper,
  ModelsApiHelper as DatanacModelsApiHelper,
  UsersApiHelper as DatanacUsersApiHelper,
  RmaApiHelper as DatatacRmaApiHelper,
} from "@datanac/datanac-api-toolkit";
import Axios from "axios";
import { buildStorage, setupCache } from "axios-cache-interceptor";
import localforage from "localforage";

import { AmplifySessionManager as SessionManager } from "./AmplifySessionManager";

// --- --- ---

const TTL_12_HOURS_MS = 12 * 60 * 60 * 1000;

export const InsurancePlanHelper = new Datanac_InsurancePlanHelper();

export const _sessionManager = new SessionManager();

export const storage = localforage.createInstance({
  driver: [localforage.INDEXEDDB, localforage.LOCALSTORAGE],
  name: "com.app.tilley-axios-cache-v1",
});

const localForage = buildStorage({
  // Return undefined if cache miss or error - will trigger real HTTP request
  find: (key) =>
    storage
      .getItem(key)
      .then((val) => (val === null ? undefined : val))
      .catch(() => undefined),
  // Return null on error - cache will be skipped for this request
  set: (key, value) => storage.setItem(key, value).catch(() => null),
  // Return null on error - old cache might remain but will expire by TTL
  remove: (key) => storage.removeItem(key).catch(() => null),
});

const AIP_PASS_ADMIN_PATH_PREFIXES = ["aip-pass/vendor", "aip-pass/files"];

const __instance = setupCache(
  Axios.create({
    baseURL: import.meta.env.VITE_DATANAC_AIP_PASS_API_URL_BASE,
  }),
  {
    methods: ["get", "post"],
    storage: localForage,
    cacheTakeover: false,
    // Custom cache key generator that includes request body for POST requests
    generateKey: (req) => {
      const method = req.method?.toUpperCase() || "GET";
      const url = req.url || "";
      // Include request body in cache key to differentiate POST requests with different payloads
      const body = req.data ? JSON.stringify(req.data) : "";
      return `${method}:${url}:${body}`;
    },
    headerInterpreter: () => {
      return TTL_12_HOURS_MS + 1;
    },
  }
);

// AIP PASS admin dashboard must reflect fresh S3 README/file listings after imports.
__instance.interceptors.request.use((config) => {
  const url = config.url ?? "";
  if (AIP_PASS_ADMIN_PATH_PREFIXES.some((prefix) => url.includes(prefix))) {
    config.cache = false;
  }
  return config;
});

export const ApiHelper = new DatanacApiHelper({
  baseUrl: import.meta.env.VITE_DATANAC_AIP_PASS_API_URL_BASE,
  config: {},
  sessionManager: _sessionManager,
  _instance: __instance,
});

export const AipPassApiHelper = new DatanacAipPassApiHelper({
  baseUrl: import.meta.env.VITE_DATANAC_AIP_PASS_API_URL_BASE,
  config: {},
  sessionManager: _sessionManager,
  _instance: __instance,
});

export const RmaApiHelper = new DatatacRmaApiHelper({
  baseUrl: import.meta.env.VITE_DATANAC_AIP_PASS_API_URL_BASE,
  config: {},
  sessionManager: _sessionManager,
  _instance: __instance,
});

export const ModelsApiHelper = new DatanacModelsApiHelper({
  baseUrl: import.meta.env.VITE_DATANAC_AIP_PASS_API_URL_BASE,
  config: {},
  sessionManager: _sessionManager,
  _instance: __instance,
});

// No caching:
export const UsersApiHelper = new DatanacUsersApiHelper({
  baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
  config: {},
  sessionManager: _sessionManager,
  // No caching:
  _instance: null,
});

// Direct API calls for Users API (for custom endpoints like /marketing/Member)
export const UsersApiHelperForMember = new DatanacApiHelper({
  baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
  config: {},
  sessionManager: _sessionManager,
  _instance: null,
});
